// InventoryCloseDecision.js
import React from 'react';
import dayjs from 'dayjs';
import {
  Box, ButtonGroup, Paper, TextField, MenuItem, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Badge, Stack, Alert
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import Client from '../Client';
import Config from '../ClientConfig';

const QUANTITY_INPUT_PROPS = {
  min: 0,
  step: 1,
  inputMode: 'decimal'
};
const round4 = (n) => Number(Number(n || 0).toFixed(4));
const preventWheel = (e) => e.currentTarget.blur();
const limitTo4Decimals = (value) => {
  let val = String(value ?? '').replace(/[^0-9.]/g, '');
  const parts = val.split('.');
  if (parts.length > 2) return null;
  if (parts.length === 2) return `${parts[0]}.${parts[1].slice(0, 4)}`;
  return parts[0];
};
const compactFieldSx = {
  minWidth: {
    xs: 'calc(50% - 6px)',
    sm: 150,
  },
  flex: {
    xs: '1 1 calc(50% - 6px)',
    sm: '0 0 auto',
  },
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};
const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};
const compactFilterPaperSx = {
  flexShrink: 0,
  p: {
    xs: 0.5,
    sm: 0.75,
  },
  mb: 0.75,
  borderRadius: '14px',
  overflow: 'hidden',
};
const compactTablePaperSx = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '14px',
};
const compactTableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  width: '100%',
};
const compactTableSx = {
  minWidth: {
    xs: 760,
    sm: 860,
    md: '100%',
  },
  '& .MuiTableCell-stickyHeader': {
    backgroundColor: '#f8fafc',
    zIndex: 3,
  },
  '& .MuiTableCell-root': {
    py: { xs: 0.45, sm: 0.65 },
    px: { xs: 0.5, sm: 0.9 },
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
    whiteSpace: 'nowrap',
  },
};
const compactPaginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

class InventoryCloseDecision extends React.Component {
  constructor(props) {
    super(props);

    const thisMonth = dayjs().format('YYYY-MM');

    this.state = {
      client: new Client('none'),
      loading: false,
      saving: false,
      applyErrorOpen: false,
      applyErrorDetail: '',

      // lookups
      locations: [],
      products: [],
      productNameMap: {},

      // filters
      filters: { period: thisMonth, location: '', product: '' },

      // data
      all_rows: [],
      rows: [],
      edited: new Set(),
      invalid: new Set(),     // rows failing inline validation
      EPS: 0.00005,           // tolerance for Δ=0

      // control which deltas are included when saving / viewing
      deltaSave: 'all',       // 'all' | 'positive' | 'negative'

      // ui
      page: 0,
      rowsPerPage: -1,

      // modal
      adjustmentDialogOpen: false,
      adjustmentResult: null,
      adjustmentError: null,

      // retry cache
      lastPayload: null,

      // flags/warnings
      hasInventoryCheck: false,     // true if snapshot contains any non-null checkQty
      hasNegUnadjusted: false,      // any calcQty < 0 with |Δ| ≈ 0
    };
  }

  // ---------- lifecycle ----------
  componentDidMount() {
    this.fetchLookups();
    this.beforeUnloadHandler = (e) => {
      if (this.state.edited && this.state.edited.size > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }
  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  // ---------- utils ----------
  nf = (val, maxDigits = 4) => {
    if (val == null || val === '') return '';
    const num = Number(val);
    if (!Number.isFinite(num)) return val;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDigits,
    });
  };
  toFixedOrEmpty(n, digits = 2) {
    return (n === null || n === undefined || isNaN(Number(n))) ? '' : Number(n).toFixed(digits);
  }
  escapeCSV(value) {
    const s = (value ?? '').toString();
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }
  sanitizeNumber = (v, dp = 4) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return Number(n.toFixed(dp));
  };

  // Single source of truth for delta: decision - calc
  deltaFromRow = (r) => Number(r.decisionQty) - Number(r.calcQty);

  getProductName = (prodId) => {
    if (!prodId) return '';
    const map = this.state.productNameMap || {};
    return map[String(prodId)] || String(prodId);
  };
  applyProductFilter(rows, productId) {
    if (!productId) return rows;
    return (rows || []).filter(r => String(r.productId) === String(productId));
  }

  // PURE: view filter by delta sign; pass explicit mode to avoid stale state
  applyDeltaFilter = (rows, deltaMode = this.state.deltaSave, EPS = this.state.EPS) => {
    if (deltaMode === 'all') return rows || [];
    return (rows || []).filter(r =>
      deltaMode === 'positive' ? r.diff > EPS : r.diff < -EPS
    );
  };

  openApplyError = (detail) => {
    this.setState({ applyErrorOpen: true, applyErrorDetail: detail || 'Unknown error' });
  };

  validateFiltersStrict = () => {
    const { period, location } = this.state.filters;
    if (!period || !/^\d{4}-\d{2}$/.test(period)) {
      return { ok: false, detail: 'Invalid period. Please select a YYYY-MM month.' };
    }
    const [y, m] = period.split('-').map(Number);
    if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) {
      return { ok: false, detail: 'Invalid month. Please select a valid YYYY-MM.' };
    }
    if (!location) return { ok: false, detail: 'Please select a location.' };
    return { ok: true, year: y, month: m, location };
  };

  chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  isRowInvalid = (row) => {
    // Only "Calculated" source requires decision == calcQty
    if (Number(row.source) === 0) {
      const diff = Math.abs(this.deltaFromRow(row));
      return diff > this.state.EPS;
    }
    return false;
  };

  blurActive = () => {
    try {
      const el = document.activeElement;
      if (el && typeof el.blur === 'function') el.blur();
    } catch (_) {}
  };

  // ---------- lookups ----------
  fetchLookups() {
    this.setState({ loading: true });
    const locationsResp = this.state.client.getData(Config.API_URL_LOCATIONS + 'list_locations/', { status: 1 }) || {};
    const productsResp  = this.state.client.getData(Config.API_URL_PRODUCT + 'list_products/', { status: 2, orderBy_name: true }) || {};

    const locations = (Array.isArray(locationsResp.locations) ? locationsResp.locations : [])
      .sort((a, b) => {
        const an = (a.name || a.description || '').toLowerCase();
        const bn = (b.name || b.description || '').toLowerCase();
        return an.localeCompare(bn);
      });
    const products  = Array.isArray(productsResp.products) ? productsResp.products : [];

    const productNameMap = {};
    products.forEach((p) => {
      const pid = p.iref || p.uid || p.code || p.id || p.product;
      const pname = p.name || p.productName || p.description || p.code || pid;
      if (pid) productNameMap[String(pid)] = String(pname);
    });

    this.setState({ locations, products, productNameMap, loading: false }, () => {
      // Only auto-fetch if a location is already selected
      if (this.state.filters.location) this.fetchData();
    });
  }

  // ---------- query ----------
  buildQuery() {
    const { period, location } = this.state.filters;
    const [yearStr, monthStr] = (period || '').split('-');
    const q = { year: yearStr, month: monthStr };
    if (location) q.location = location;
    return q;
  }

  // ---------- fetch snapshot ----------
  fetchData() {
    const { client, filters, productNameMap, EPS } = this.state;
    if (!filters.location) { // guard against accidental call with no location
      this.setState({ rows: [], all_rows: [], page: 0, hasInventoryCheck: false, hasNegUnadjusted: false });
      return;
    }

    this.setState({ loading: true, page: 0 });

    const q = this.buildQuery();
    const resp = client.getData(
      Config.API_URL_REPORTS_TRANSACTIONS + 'inventory_close_snapshot/',
      q
    ) || {};

    if (resp.error || (resp.detail && !Array.isArray(resp.snapshot))) {
      this.setState({ loading: false });
      this.openApplyError(resp.detail || 'Snapshot request failed.');
      return;
    }
    const list = Array.isArray(resp.snapshot) ? resp.snapshot : [];

    // Build rows
    let flattened = list.map((ln) => {
      const pid = String(ln.product);
      const calcQty = Number(ln.calcQty || 0);
      const checkQty = (ln.checkQty == null) ? null : Number(ln.checkQty);
      const decisionQty = (ln.decisionQty != null) ? Number(ln.decisionQty) : calcQty;
      const source = (ln.source != null) ? Number(ln.source) : (checkQty != null ? 1 : 0);
      const row = {
        id: ln.decisionId || pid,
        decisionId: ln.decisionId || null,
        productId: pid,
        productName: ln.productName || productNameMap[pid] || pid,
        calcQty,
        checkQty,
        decisionQty,
        source,
      };
      row.diff = this.deltaFromRow(row);
      return row;
    });

    // Flag: do we have an inventory check in this snapshot?
    const hasInventoryCheck = flattened.some(r => r.checkQty != null);

    // 1) Remove rows where both calc≈0 and check is null/≈0
    flattened = flattened.filter(r => {
      const calcZero = Math.abs(Number(r.calcQty)) < EPS;
      const checkZeroOrNull = (r.checkQty == null) || (Math.abs(Number(r.checkQty)) < EPS);
      return !(calcZero && checkZeroOrNull);
    });

    // 2) Sort so negative calcQty show first
    flattened.sort((a, b) => {
      const aNeg = a.calcQty < 0, bNeg = b.calcQty < 0;
      if (aNeg && !bNeg) return -1;
      if (!aNeg && bNeg) return 1;
      // tie-breaker by product name
      const an = (a.productName || '').toLowerCase();
      const bn = (b.productName || '').toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });

    // 3) Warning if we have negative calc and Δ ≈ 0 (unadjusted negatives)
    const hasNegUnadjusted = flattened.some(r => (r.calcQty < 0) && (Math.abs(r.diff) < EPS));

    // recompute invalid set
    const invalid = new Set();
    flattened.forEach(r => { if (this.isRowInvalid(r)) invalid.add(r.id); });

    // apply product + delta filters to what we SHOW
    const rows = this.applyDeltaFilter(
      this.applyProductFilter(flattened, filters.product),
      this.state.deltaSave,
      this.state.EPS
    );

    this.setState({
      all_rows: flattened,
      rows,
      loading: false,
      edited: new Set(),
      invalid,
      hasInventoryCheck,
      hasNegUnadjusted,
    });
  }

  // ---------- filters events ----------
  setFilter = (name, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [name]: value } }));
  };

  onApply = () => {
    const { location } = this.state.filters;
    if (!location) {
      this.openApplyError('Please select a location before applying.');
      return;
    }
    try {
      this.fetchData();
    } catch (e) {
      this.openApplyError(e?.message || 'Failed to load snapshot.');
    }
  };

  // ---------- pagination ----------
  onChangePage = (_e, newPage) => this.setState({ page: newPage });
  onChangeRowsPerPage = (e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 });

  // ---------- local edit helpers ----------
  // (2) When a single qty is edited, only that row goes to `edited` (kept)
  markEdited = (id, changes) => {
    this.setState(prev => {
      const apply = (r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...changes };
        const newDQ = ('decisionQty' in changes) ? Number(changes.decisionQty) : Number(r.decisionQty);
        next.decisionQty = newDQ;
        next.diff = this.deltaFromRow(next); // always vs calcQty
        return next;
      };

      const all_rows = prev.all_rows.map(apply);

      const edited = new Set(prev.edited);
      edited.add(id);

      const updatedRow = all_rows.find(x => x.id === id);
      const invalid = new Set(prev.invalid);
      if (this.isRowInvalid(updatedRow)) invalid.add(id); else invalid.delete(id);

      // Recompute visible rows through filters (product + delta)
      const base = this.applyProductFilter(all_rows, prev.filters.product);
      const rows = this.applyDeltaFilter(base, prev.deltaSave, prev.EPS);

      return { rows, all_rows, edited, invalid };
    });
  };

  // (4) All Inventory: if snapshot has an inventory check, rows missing checkQty get 0
  setAllSource = (source) => {
    this.setState(prev => {
      const hasCheck = prev.hasInventoryCheck;
      const mapSource = (r) => {
        let decisionQty = r.decisionQty;
        if (source === 0) {
          decisionQty = round4(r.calcQty);
        } else {
          if (r.checkQty != null) {
            decisionQty = round4(r.checkQty);
          } else {
            decisionQty = hasCheck ? 0 : round4(r.calcQty);
          }
        }
        const next = { ...r, source, decisionQty };
        next.diff = this.deltaFromRow(next);
        return next;
      };

      const all_rows = prev.all_rows.map(mapSource);

      // rebuild invalid set (Calculated requires Δ=0)
      const invalid = new Set();
      all_rows.forEach(rr => { if (this.isRowInvalid(rr)) invalid.add(rr.id); });

      // Bulk action = mark all edited
      const edited = new Set(all_rows.map(r => r.id));

      // Recompute visible rows through filters
      const base = this.applyProductFilter(all_rows, prev.filters.product);
      const rows = this.applyDeltaFilter(base, prev.deltaSave, prev.EPS);

      // Re-check the negative-unadjusted banner after bulk change
      const hasNegUnadjusted = all_rows.some(r => (r.calcQty < 0) && (Math.abs(r.diff) < prev.EPS));

      return { rows, all_rows, edited, invalid, hasNegUnadjusted };
    });
  };

  // ---------- decisions payload ----------
  buildDecisionsPayload = () => {
    const { all_rows, edited, EPS, deltaSave } = this.state;

    // Smart scope: if nothing edited, consider all rows; else only edited rows
    const candidateRows = (edited && edited.size > 0)
      ? all_rows.filter(r => edited.has(r.id))
      : all_rows;

    const decisions = [];

    candidateRows.forEach((r) => {
      const source = Number(r.source);                 // 0 = calc, 1 = inventory
      const calcQty = Number(r.calcQty ?? 0);
      const decisionQty = Number(r.decisionQty ?? calcQty);
      const delta = this.deltaFromRow(r);              // decisionQty - calcQty (signed)

      // Skip near-zero deltas
      if (Math.abs(delta) < EPS) return;

      // Respect selector sign
      const want =
        deltaSave === 'all' ||
        (deltaSave === 'positive' && delta > EPS) ||
        (deltaSave === 'negative' && delta < -EPS);

      if (!want) return;

      // Safety: skip invalid "Calculated" rows (Δ must be 0 there)
      if (this.isRowInvalid(r)) return;

      decisions.push({
        product: String(r.productId),
        source,
        calcQty: Number(calcQty.toFixed(4)),
        decisionQty: Number(decisionQty.toFixed(4)),
        delta: Number(delta.toFixed(4)),
      });
    });

    return decisions;
  };

  // exact save-set count
  getSaveCount = () => this.buildDecisionsPayload().length;

  // save tooltip preview (lines, sum +/−, net)
  getSavePreview = () => {
    const { EPS } = this.state;
    const decisions = this.buildDecisionsPayload();
    let sumPos = 0, sumNeg = 0;
    decisions.forEach(d => {
      if (d.delta > EPS) sumPos += d.delta;
      else if (d.delta < -EPS) sumNeg += d.delta;
    });
    return { lines: decisions.length, sumPos, sumNeg, net: sumPos + sumNeg };
  };

  getSavePreviewTitle = () => {
    const p = this.getSavePreview();
    return (
      <Box sx={{ whiteSpace: 'pre-line' }}>
        {`${p.lines} line${p.lines === 1 ? '' : 's'} to save
ΣΔ+: ${this.nf(p.sumPos, 2)}
ΣΔ−: ${this.nf(p.sumNeg, 2)}
Net: ${this.nf(p.net, 2)}`}
      </Box>
    );
  };

  // ---------- save & register (single endpoint) ----------
  saveAll = () => {
    this.blurActive();
    if (this.state.saving) return; // prevent double submit

    const v = this.validateFiltersStrict();
    if (!v.ok) {
      this.blurActive();
      this.setState({
        adjustmentDialogOpen: true,
        adjustmentResult: null,
        adjustmentError: { detail: v.detail }
      });
      return;
    }

    if (this.state.invalid.size > 0) {
      this.setState({
        adjustmentDialogOpen: true,
        adjustmentResult: null,
        adjustmentError: { detail: `Fix ${this.state.invalid.size} invalid row(s): Calculated requires Δ=0.` }
      });
      return;
    }

    const payloadBase = { year: v.year, month: v.month, location: v.location };
    const decisions = this.buildDecisionsPayload();

    if (!decisions.length) {
      this.setState({
        adjustmentDialogOpen: true,
        adjustmentResult: null,
        adjustmentError: { detail: 'Nothing to save: all deltas are zero or filtered out.' }
      });
      return;
    }

    const batches = (decisions.length > 500) ? this.chunk(decisions, 500) : [decisions];

    this.setState({ saving: true });

    let totalIn = 0, totalOut = 0, residual = 0, upserted = 0;
    let error = null;

    for (let i = 0; i < batches.length; i++) {
      const payload = { ...payloadBase, decisions: batches[i] };
      this.setState({ lastPayload: payload });

      const resp = this.state.client.pushData(
        (Config.API_URL_INV_CLOSE_DECISION_SAVE || (Config.API_URL_REPORTS_TRANSACTIONS + 'inventoryCloseDecision/')),
        payload
      );

      if (!resp || resp.error) {
        const detail = (resp && resp.detail) ? resp.detail : `Failed on batch ${i + 1}/${batches.length}`;
        error = { detail };
        break;
      }

      upserted += Number(resp.upsertedDecisions ?? resp.upserted_decisions ?? 0);
      totalIn  += Number(resp.IN_lines ?? resp.created_in_lines ?? 0);
      totalOut += Number(resp.OUT_lines ?? resp.created_out_lines ?? 0);
      residual += Number(resp.Residual_lines ?? resp.residual_lines ?? 0);
    }

    this.setState({ saving: false });

    if (error) {
      this.setState({
        adjustmentDialogOpen: true,
        adjustmentResult: null,
        adjustmentError: error
      });
      return;
    }

    this.setState({
      adjustmentDialogOpen: true,
      adjustmentResult: {
        upserted_decisions: upserted,
        created_in_lines: totalIn,
        created_out_lines: totalOut,
        residual_lines: residual,
        recomputed: true
      },
      adjustmentError: null,
      edited: new Set()
    });
  };

  retryRegister = () => {
    this.blurActive();
    const lp = this.state.lastPayload;
    if (!lp || this.state.saving) { this.saveAll(); return; }

    this.setState({ saving: true });

    const resp = this.state.client.pushData(
      (Config.API_URL_INV_CLOSE_DECISION_SAVE || (Config.API_URL_REPORTS_TRANSACTIONS + 'inventoryCloseDecision/')),
      lp
    );

    this.setState({ saving: false });

    if (!resp || resp.error) {
      this.setState({
        adjustmentDialogOpen: true,
        adjustmentResult: null,
        adjustmentError: { detail: (resp && resp.detail) ? resp.detail : 'Retry failed' },
      });
    } else {
      this.setState({
        adjustmentDialogOpen: true,
        adjustmentResult: {
          upserted_decisions: Number(resp.upsertedDecisions ?? resp.upserted_decisions ?? 0),
          created_in_lines: Number(resp.IN_lines ?? resp.created_in_lines ?? 0),
          created_out_lines: Number(resp.OUT_lines ?? resp.created_out_lines ?? 0),
          residual_lines: Number(resp.Residual_lines ?? resp.residual_lines ?? 0),
        },
        adjustmentError: null,
        edited: new Set()
      });
    }
  };

  // ---------- CSV export (exports exactly what Save would send) ----------
  exportCSV = () => {
    const {
      filters, all_rows, EPS, deltaSave, locations = [], productNameMap = {}
    } = this.state;

    const decisions = this.buildDecisionsPayload();

    let sumPos = 0, sumNeg = 0;
    decisions.forEach(d => {
      if (d.delta > EPS) sumPos += d.delta;
      else if (d.delta < -EPS) sumNeg += d.delta;
    });
    const net = sumPos + sumNeg;

    const periodLabel = filters.period || '';
    const locId = filters.location;
    const locObj = (locations || []).find(
      l => String(l.iref ?? l.uid ?? l.id) === String(locId)
    );
    const locationLabel = locId
      ? (locObj?.name || locObj?.description || String(locId))
      : 'All';

    const productLabel = filters.product
      ? (productNameMap[String(filters.product)] || String(filters.product))
      : 'All';

    const scopeLabel =
      deltaSave === 'positive' ? 'Only Δ > 0'
      : deltaSave === 'negative' ? 'Only Δ < 0'
      : 'All deltas';

    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');

    const summaryRows = [
      ['Summary'],
      ['Period', periodLabel],
      ['Location', locationLabel],
      ['Product filter', productLabel],
      ['Δ scope', scopeLabel],
      ['Lines to save', String(decisions.length)],
      ['Total Δ+', this.toFixedOrEmpty(sumPos, 4)],
      ['Total Δ−', this.toFixedOrEmpty(sumNeg, 4)],
      ['Net Δ', this.toFixedOrEmpty(net, 4)],
      ['Generated at', generatedAt],
      ['']
    ].map(row => row.map(v => this.escapeCSV(v)).join(','));

    const header = ['Product','Calc Qty','Check Qty','Source','Decision Qty','Delta'].join(',');

    const lines = decisions.map(d => {
      const r = all_rows.find(rr => String(rr.productId) === String(d.product)) || {};
      const productNameOnly = r.productName || productNameMap[String(d.product)] || String(d.product);
      const checkQty = (r.checkQty == null) ? '' : this.toFixedOrEmpty(r.checkQty, 4);
      const sourceLabel = d.source === 0 ? 'Calculated' : 'Inventory';

      return [
        this.escapeCSV(productNameOnly),
        this.toFixedOrEmpty(d.calcQty, 4),
        checkQty,
        sourceLabel,
        this.toFixedOrEmpty(d.decisionQty, 4),
        this.toFixedOrEmpty(d.delta, 4),
      ].join(',');
    });

    const csv = [...summaryRows, header, ...lines].join('\r\n');

    const periodSafe = (periodLabel || 'period').replace(/[^0-9-]/g, '');
    const locSafe = (locationLabel || 'location').replace(/[^a-z0-9-_]+/gi, '_');
    const fname = `InventoryCloseDecision_SAVESET_${periodSafe}_${locSafe}.csv`;

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------- render ----------
  renderFilters() {
    const { filters, locations, products } = this.state;

    const productOptions = [
      { label: 'All', value: '' },
      ...(products || []).map(p => {
        const value = String(p.iref ?? p.uid ?? p.code ?? p.id ?? p.product ?? '');
        const label = p.name ?? p.productName ?? p.description ?? p.code ?? value;
        return { value, label };
      }),
    ];

    const selectedVal = String(filters?.product ?? '');
    const productValue =
      productOptions.find(o => o.value === selectedVal) ?? productOptions[0];

    return (
      <Paper elevation={1} sx={compactFilterPaperSx}>
        <Stack
          direction="row"
          sx={{
            gap: { xs: 0.75, sm: 1 },
            '& .MuiButton-root': compactButtonSx,
            '& .MuiButtonGroup-root .MuiButton-root': {
              height: 32,
              borderRadius: 0,
            },
            '& .MuiButtonGroup-root .MuiButton-root:first-of-type': {
              borderTopLeftRadius: '11px',
              borderBottomLeftRadius: '11px',
            },
            '& .MuiButtonGroup-root .MuiButton-root:last-of-type': {
              borderTopRightRadius: '11px',
              borderBottomRightRadius: '11px',
            },
          }}
          flexWrap="wrap"
          alignItems="center"
        >
          <TextField
            label="Period"
            type="month"
            value={filters.period}
            onChange={(e) => this.setFilter('period', e.target.value)}
            size="small"
            margin="dense"
            sx={compactFieldSx}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            select
            label="Location"
            value={filters.location}
            onChange={(e) => this.setFilter('location', e.target.value)}
            size="small"
            margin="dense"
            sx={compactFieldSx}
          >
            <MenuItem value="">All</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={String(loc.iref || loc.uid || loc.id)} value={String(loc.iref || loc.uid || loc.id)}>
                {loc.name || loc.description || String(loc.iref || loc.uid || loc.id)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ flexGrow: 1, minWidth: 8 }} />

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              flexWrap: 'wrap',
              width: {
                xs: '100%',
                md: 'auto',
              },
            }}
          >
            <ButtonGroup size="small" variant="outlined">
              <Button variant="contained" onClick={this.onApply} disabled={this.state.loading}>Apply</Button>
              <Button onClick={() => this.setAllSource(0)}>All Calc</Button>
              <Button onClick={() => this.setAllSource(1)}>All Inv</Button>
            </ButtonGroup>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={this.state.deltaSave}
              onChange={(_e, deltaSave) => {
                if (!deltaSave) return; // prevent deselect
                this.setState(prev => {
                  const base = this.applyProductFilter(prev.all_rows || [], prev.filters.product);
                  const rows = this.applyDeltaFilter(base, deltaSave, prev.EPS);
                  return { deltaSave, rows, page: 0 };
                });
              }}
              aria-label="Delta filter mode"
              sx={{
                '& .MuiToggleButton-root': {
                  fontWeight: 600,
                  minWidth: { xs: 36, sm: 44 },
                  height: 32,
                  px: { xs: 0.75, sm: 1 },
                },
                '& .MuiToggleButton-root.Mui-selected': { color: 'common.white' },
                '& .MuiToggleButton-root[value="all"].Mui-selected': {
                  bgcolor: 'primary.main', borderColor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                },
                '& .MuiToggleButton-root[value="positive"].Mui-selected': {
                  bgcolor: 'success.main', borderColor: 'success.main',
                  '&:hover': { bgcolor: 'success.dark' },
                },
                '& .MuiToggleButton-root[value="negative"].Mui-selected': {
                  bgcolor: 'error.main', borderColor: 'error.main',
                  '&:hover': { bgcolor: 'error.dark' },
                },
              }}
            >
              <ToggleButton value="all" aria-label="All deltas">All</ToggleButton>
              <ToggleButton value="positive" aria-label="Positive deltas">+</ToggleButton>
              <ToggleButton value="negative" aria-label="Negative deltas">−</ToggleButton>
            </ToggleButtonGroup>

            <Tooltip title={this.getSavePreviewTitle()} arrow>
              <span style={{ display: 'inline-flex' }}>
                <Badge
                  badgeContent={this.getSaveCount()}
                  color={this.getSaveCount() ? 'warning' : 'default'}
                >
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    onClick={this.saveAll}
                    disabled={this.state.saving || this.state.invalid.size > 0}
                    disableElevation
                    sx={{ minWidth: 90 }}
                  >
                    {this.state.saving ? 'Saving…' : 'Save'}
                  </Button>
                </Badge>
              </span>
            </Tooltip>

            <Button variant="outlined" size="small" onClick={this.exportCSV} title="Exports exactly what Save would send">
              CSV
            </Button>
          </Stack>

          <Autocomplete
            size="small"
            sx={{
              flex: {
                xs: '1 1 100%',
                sm: '0 0 260px',
              },
              minWidth: 0,
              maxWidth: {
                xs: '100%',
                sm: 340,
              },
            }}
            value={productValue}
            options={productOptions}
            isOptionEqualToValue={(o, v) => (o?.value ?? '') === (v?.value ?? '')}
            onChange={(_e, opt) => {
              const newVal = opt ? opt.value : '';
              this.setFilter('product', newVal);
              const filtered = this.applyDeltaFilter(
                this.applyProductFilter(this.state.all_rows || [], newVal),
                this.state.deltaSave,
                this.state.EPS
              );
              this.setState({ rows: filtered, page: 0 });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Product"
                margin="dense"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    minHeight: 34,
                    borderRadius: '12px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: {
                      xs: '16px',
                      sm: '0.84rem',
                    },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'left',
                  },
                }}
                InputProps={{ ...params.InputProps }}
                inputProps={{ ...params.inputProps, title: params.inputProps?.value }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} title={option.label}>
                <div
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    maxWidth: 300,
                  }}
                >
                  {option.label}
                </div>
              </li>
            )}
          />
        </Stack>
      </Paper>
    );
  }

  renderTable() {
    const { loading, rows, page, rowsPerPage, edited, invalid, hasNegUnadjusted } = this.state;
    const paged = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

    return (
      <Paper elevation={1} sx={compactTablePaperSx}>
        {/* (3) Warning banner for negative calc qty not adjusted */}
        {hasNegUnadjusted && (
          <Alert
            severity="warning"
            sx={{
              flexShrink: 0,
              mx: { xs: 0.75, sm: 1 },
              mt: { xs: 0.75, sm: 1 },
              mb: 0.75,
              py: { xs: 0.25, sm: 0.5 },
              fontSize: { xs: '0.76rem', sm: '0.84rem' },
            }}
          >
            There are products with <b>negative calculated quantity</b> and <b>no adjustment (Δ≈0)</b>. Review and adjust them if needed.
          </Alert>
        )}

        <TableContainer sx={compactTableContainerSx}>
          <Table stickyHeader size="small" sx={compactTableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Calc Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Check Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Source</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Decision Qty</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Δ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map((row, idx) => {
                const diffColor = row.diff > 0 ? 'green' : row.diff < 0 ? 'red' : undefined;
                const bgEdited = edited.has(row.id) ? 'rgba(255,255,0,0.18)' : undefined;
                const isInvalid = invalid.has(row.id);

                return (
                  <TableRow key={row.id || `${page}-${idx}`} hover sx={{ backgroundColor: bgEdited }}>
                    <TableCell>
                      <Tooltip title={row.productId} placement="top" arrow>
                        <span>{row.productName || row.productId}</span>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="right">{this.nf(row.calcQty, 4)}</TableCell>
                    <TableCell align="right">{row.checkQty == null ? '' : this.nf(row.checkQty, 4)}</TableCell>

                    <TableCell align="right">
                      <TextField
                        select size="small" value={row.source}
                        onChange={(e) => {
                          const nextSource = Number(e.target.value);
                          let nextDecisionQty;
                          if (nextSource === 0) {
                            nextDecisionQty = row.calcQty;
                            const diff = Math.abs(Number(nextDecisionQty) - Number(row.calcQty));
                            if (diff > this.state.EPS) return; // block invalid selection
                          } else {
                            // Inventory: prefer check; if snapshot has check but this item is missing, use 0
                            if (row.checkQty != null) nextDecisionQty = row.checkQty;
                            else nextDecisionQty = this.state.hasInventoryCheck ? 0 : row.calcQty;
                          }

                          this.markEdited(row.id, {
                            source: nextSource,
                            decisionQty: round4(nextDecisionQty),
                          });
                        }}
                        sx={{
                          minWidth: { xs: 104, sm: 120 },
                          '& .MuiInputBase-root': {
                            height: 30,
                          },
                          '& .MuiInputBase-input': {
                            fontSize: { xs: '0.76rem', sm: '0.84rem' },
                          },
                        }}
                        error={isInvalid}
                        helperText={isInvalid ? 'Calculated requires Δ=0' : ''}
                      >
                        <MenuItem value={0}>Calculated</MenuItem>
                        <MenuItem value={1}>Inventory</MenuItem>
                      </TextField>
                    </TableCell>

                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={row.decisionQty ?? 0}
                        onChange={(e) => {
                          let value = e.target.value;

                          if (value === '') {
                            this.markEdited(row.id, { decisionQty: 0 });
                            return;
                          }

                          // block scientific notation and invalid characters
                          if (!/^\d*\.?\d*$/.test(value)) return;

                          // trim to max 4 decimals
                          if (value.includes('.')) {
                            const [intPart, decPart] = value.split('.');
                            value = `${intPart}.${(decPart || '').slice(0, 4)}`;
                          }

                          this.markEdited(row.id, {
                            decisionQty: Number(value),
                          });
                        }}
                        sx={{
                          maxWidth: { xs: 96, sm: 130 },
                          '& .MuiInputBase-root': {
                            height: 30,
                          },
                          '& .MuiInputBase-input': {
                            fontSize: {
                              xs: '16px',
                              sm: '0.84rem',
                            },
                            textAlign: 'right',
                            px: 0.5,
                          },
                        }}
                        inputProps={{
                          ...QUANTITY_INPUT_PROPS,
                          step: '0.0001',
                        }}
                        onWheel={preventWheel}
                        error={isInvalid}
                        helperText={isInvalid ? 'Calculated requires Δ=0' : ''}
                      />
                    </TableCell>

                    <TableCell align="right" sx={{ color: diffColor }}>
                      {this.nf(row.diff, 4)}
                    </TableCell>
                  </TableRow>
                );
              })}

              {!paged.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center">No data</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={this.onChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={this.onChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={compactPaginationSx}
        />

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    );
  }

  renderAdjustmentDialog() {
    const { adjustmentDialogOpen, adjustmentResult, adjustmentError } = this.state;
    return (
      <Dialog
        open={adjustmentDialogOpen}
        fullWidth
        maxWidth="sm"
        onClose={() => {}}
        disableEscapeKeyDown
        container={() => document.getElementById('root')}
        keepMounted
      >
        <DialogTitle>Stock Adjustments</DialogTitle>
        <DialogContent dividers>
          {adjustmentResult && (
            <Box>
              <Typography variant="body1" color="success.main">Decisions saved and adjustments registered.</Typography>
              <Typography sx={{ mt: 1 }}>
                Upserted decisions: {adjustmentResult.upserted_decisions ?? 0}
              </Typography>
              <Typography>IN lines: {adjustmentResult.created_in_lines}</Typography>
              <Typography>OUT lines: {adjustmentResult.created_out_lines}</Typography>
              {typeof adjustmentResult.residual_lines === 'number' && (
                <Typography>Residual lines: {adjustmentResult.residual_lines}</Typography>
              )}
              <Typography variant="body2" sx={{ mt: 2 }}>
                Re-run month close to refresh calculated quantities.
              </Typography>
            </Box>
          )}

          {adjustmentError && (
            <Box>
              <Typography variant="body1" color="error.main">Registration failed.</Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                {adjustmentError.detail || JSON.stringify(adjustmentError, null, 2)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {adjustmentError ? (
            <>
              <Button onClick={this.retryRegister}>Retry</Button>
              <Button color="inherit" onClick={() => this.setState({ adjustmentDialogOpen: false })}>
                Close
              </Button>
            </>
          ) : (
            <Button onClick={() => this.setState({ adjustmentDialogOpen: false }, () => this.fetchData())}>Close</Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          bgcolor: 'background.default',
          p: {
            xs: 0.5,
            sm: 0.85,
            md: 1.1,
          },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {this.renderFilters()}
        {this.renderTable()}
        {this.renderAdjustmentDialog()}

        <Dialog
          open={this.state.applyErrorOpen}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') this.setState({ applyErrorOpen: false });
          }}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
          container={() => document.getElementById('root')}
          keepMounted
        >
          <DialogTitle>Can’t Apply Filters</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {this.state.applyErrorDetail}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ applyErrorOpen: false })}>OK</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default InventoryCloseDecision;